/**
 * The Governance_Metric identifier set and the three types every metric travels
 * in (`complan-governance/requirements.md` Requirements 1 and 2).
 *
 * This module holds the committed identifier list; `metric-registry.ts` holds
 * the definition per identifier and the boot-time consistency assertion. The
 * split is Foundation's, between `validation-rule.ts` and
 * `validation-registry.ts`, and it exists for the same reason: the identifier
 * union has to be importable by a module the registry itself imports, without a
 * cycle.
 *
 * ## Why a closed union rather than a string
 *
 * {@link GOVERNANCE_METRICS} is a mapping over {@link MetricId}. A metric
 * computed without an identifier in this list has no key to occupy, and an
 * identifier listed here without a definition leaves that mapping incomplete -
 * both are compile errors. "Which metrics exist" is therefore answerable by
 * reading one array, and R2.1's "exactly one Metric_Definition per Metric_Id" is
 * a property of the type system rather than a convention.
 *
 * ## Why the envelope carries numerator and denominator *and* the value
 *
 * R1.12 forbids the client computing a share from separately retrieved counts,
 * so the server computes {@link MetricValue.value}. R1.9 requires the basis to
 * travel so a reader can see what the figure was computed over, and R2.4
 * requires the numerator and denominator to be explainable. Carrying all of it
 * costs two integers and removes the only way the two could disagree.
 *
 * The zero-denominator case is the reason `value` is nullable. R1.7 requires an
 * undefined state rather than zero, and R11.4 forbids a non-finite value ever
 * reaching a screen. {@link MetricState} makes that structural: `value` is
 * non-null exactly when the state is `defined`, so there is no representable
 * `MetricValue` carrying `Infinity` or `NaN` - the division that would produce
 * one is never performed (see `metric-value.ts`).
 */
/**
 * Every Governance_Metric this specification implements, in the order the
 * registry declares them.
 *
 * Grouped by unit, because the unit decides whether an entry carries a
 * denominator (R2.3) and whether it is Viewer_Scoped (R1.4): the four `percent`
 * entries at the end are exactly the Viewer_Scoped set, and every other entry is
 * an absolute count or an absolute hours total.
 *
 * PRD 9.2 additionally names a confidential share and a yearly trend. The
 * confidential share is deliberately absent: R4.2's KPI list does not include
 * it, and adding it here without a criterion to cite would be an unrequested
 * metric. The yearly trend (R4.3) is not one metric but the same three metrics
 * recomputed per planning year, so it needs no identifier of its own.
 */
export const GOVERNANCE_METRIC_IDS = Object.freeze([
    // --- counts (R3.2-R3.7, R4.2, R4.4) ---
    'initiative_count',
    'readiness_complete_initiative_count',
    'readiness_overdue_initiative_count',
    'readiness_due_initiative_count',
    'readiness_missing_initiative_count',
    'readiness_not_applicable_initiative_count',
    'changed_after_approval_initiative_count',
    'unresolved_watchout_initiative_count',
    'unresolved_watchout_count',
    'high_severity_unresolved_watchout_initiative_count',
    'dte_involvement_initiative_count',
    'csc_involvement_initiative_count',
    'open_dependency_initiative_count',
    'overdue_dependency_count',
    'governance_exception_initiative_count',
    'warning_exception_initiative_count',
    'ownership_gap_initiative_count',
    'over_capacity_period_count',
    // --- hours (R3.6, R4.2, R5.1) ---
    'excess_demand_hours',
    'demand_hours_total',
    // --- shares: the Viewer_Scoped set (R1.4, R3.2, R3.3, R4.2, R8.7) ---
    'priority_share',
    'finality_share',
    'milestone_readiness_share',
    'ff_utilization',
]);
/**
 * The i18n keys of the exclusions R2.5 to R2.7 require stated.
 *
 * Shared constants rather than per-metric literals, because the same exclusion
 * applies to many metrics and a reader comparing two tooltips should see the
 * same sentence, not two paraphrases of it.
 */
export const METRIC_EXCLUSION_KEYS = Object.freeze({
    /** R2.5: Cancelled records are excluded unless the Cancelled status filter is selected. */
    cancelled: 'governance.exclusion.cancelled',
    /** R2.6: records the reader may not see are excluded. */
    unreadable: 'governance.exclusion.unreadable',
    /** R2.7: the Manpower Data_Incomplete handling and the resolved assumption versions. */
    dataIncomplete: 'governance.exclusion.dataIncomplete',
    /** R8.8: an Initiative holding no Required_Milestone is outside the readiness denominator. */
    noRequiredMilestone: 'governance.exclusion.noRequiredMilestone',
});
//# sourceMappingURL=metric.js.map